<template>
	<widget-settings-layout ref="layout" v-if="settings" slug="scroller" name="Scroller" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card title="Scroller" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group :label="speedLabel">
						<b-form-input type="range" min="-300" max="300" step="1" v-model.number="settings.speed" />
					</b-form-group>
					<small class="text-muted">Positive = scroll left, negative = scroll right</small>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Number of items">
						<b-form-input type="number" min="1" max="50" v-model.number="settings.itemCount"></b-form-input>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Separator">
						<b-form-input v-model="settings.separator" placeholder="·"></b-form-input>
					</b-form-group>
				</b-col>
				<b-col class="d-flex align-items-center">
					<b-form-checkbox switch v-model="settings.donationsOnly">Donations only</b-form-checkbox>
				</b-col>
			</b-form-row>
		</b-card>
		<text-settings :settings="settings" no-align />
		<b-card title="Appearance" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text color">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Item background">
						<color-picker v-model="settings.itemBg" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Top padding">
						<b-input-group append="px">
							<b-form-input type="number" min="0" v-model.number="settings.paddingTop"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Bottom padding">
						<b-input-group append="px">
							<b-form-input type="number" min="0" v-model.number="settings.paddingBottom"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Outline &amp; Shadow" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Blur">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model.number="settings.blurRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Spread">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="50" step="any" v-model.number="settings.spreadRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Color">
						<color-picker v-model="settings.shadowColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<b-form-group label="Extra CSS" class="mb-0">
				<b-textarea v-model="settings.extraCSS"></b-textarea>
			</b-form-group>
		</b-card>
		<template #preview>
			<scroller-widget-preview :settings="settings || {}" />
		</template>
	</widget-settings-layout>
</template>

<script>
import ColorPicker from '@/components/ColorPicker.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import TextSettings from '@/components/Widget/TextSettings.vue';
import ScrollerWidgetPreview from '@/components/Widget/ScrollerWidgetPreview.vue';

export default {
	components: {
		ColorPicker,
		WidgetSettingsLayout,
		TextSettings,
		ScrollerWidgetPreview,
	},
	beforeRouteLeave(to, from, next) {
		if(!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		settings: null,
		savedSettingsJson: null,
	}; },
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.loading++;
			this.error = null;
			this.saveError = null;
			const s = await this.$api.get('widgetSettings/scroller');
			this.settings = {
				speed:             s.speed ?? 60,
				fontFamily:        s.fontFamily        || '',
				fontSize:          s.fontSize          || 0,
				letterSpacing:     s.letterSpacing     || 0,
				boldText:          s.boldText          || false,
				italicText:        s.italicText        || false,
				underlineText:     s.underlineText     || false,
				strikethroughText: s.strikethroughText || false,
				uppercaseText:     s.uppercaseText     || false,
				customFont:        s.customFont        || false,
				textColor:         s.textColor         || 'rgba(255,255,255,1)',
				blurRadius:        s.blurRadius        || 0,
				spreadRadius:      s.spreadRadius      || 0,
				shadowColor:       s.shadowColor       || 'rgba(0,0,0,1)',
				itemCount:         s.itemCount         || 10,
				separator:         s.separator         !== undefined ? s.separator : '\u00b7',
				donationsOnly:     s.donationsOnly     || false,
				itemBg:            s.itemBg            || 'rgba(0,0,0,0.6)',
				paddingTop:        s.paddingTop        || 6,
				paddingBottom:     s.paddingBottom     || 6,
				extraCSS:          s.extraCSS          || '',
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	computed: {
		hasUnsavedChanges() {
			if(this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
		speedLabel() {
			if(!this.settings) return 'Speed';
			const v = this.settings.speed;
			if(v > 0)  return `Speed: ${v} px/s \u2192`;
			if(v < 0)  return `Speed: \u2190 ${Math.abs(v)} px/s`;
			return 'Speed: Stopped';
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
	},
	methods: {
		_onBeforeUnload(e) {
			if(this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		async save() {
			try {
				this.saveError = null;
				this.saving++;
				await this.$api.post('widgetSettings/scroller', { ...this.settings });
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(e) {
				console.error('Error saving widget:', e);
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
	},
};
</script>
